import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { fetchOrderById } from "../services/orderService";

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const fmt = (n) => "₹" + Number(n).toLocaleString("en-IN");

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric", month: "long", year: "numeric",
  });
};

const STATUS_STEPS = ["Confirmed", "Packed", "Shipped", "Delivered"];

const STATUS_META = {
  Confirmed: { icon: "✓", color: "#2d6a4f" },
  Packed: { icon: "📦", color: "#2d6a4f" },
  Shipped: { icon: "🚚", color: "#2d6a4f" },
  Delivered: { icon: "🎉", color: "#2d6a4f" },
  Cancelled: { icon: "✕", color: "#c0392b" },
};

/* ─────────────────────────────────────────────
   CHECKMARK ANIMATION
───────────────────────────────────────────── */
function AnimatedCheck() {
  return (
    <div className="acheck">
      <svg viewBox="0 0 52 52" className="acheck__svg">
        <circle className="acheck__circle" cx="26" cy="26" r="25" fill="none" />
        <path className="acheck__tick" fill="none" d="M14 27l8 8 16-16" />
      </svg>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ORDER TRACKER
───────────────────────────────────────────── */
function OrderTracker({ currentStatus, statusHistory }) {
  if (currentStatus === "Cancelled") {
    return (
      <div className="tracker tracker--cancelled">
        <span className="tracker__cancelled-icon">✕</span>
        <p className="tracker__cancelled-text">This order has been cancelled.</p>
      </div>
    );
  }

  const currentIdx = STATUS_STEPS.indexOf(currentStatus);

  return (
    <div className="tracker">
      {STATUS_STEPS.map((step, i) => {
        const done = i <= currentIdx;
        const active = i === currentIdx;
        const histEntry = statusHistory?.find(h => h.status === step);

        return (
          <React.Fragment key={step}>
            <div className={`tracker__step ${done ? "tracker__step--done" : ""} ${active ? "tracker__step--active" : ""}`}>
              <div className="tracker__dot">
                {done ? <span>✓</span> : <span>{i + 1}</span>}
              </div>
              <div className="tracker__info">
                <span className="tracker__label">{step}</span>
                {histEntry && (
                  <span className="tracker__time">
                    {new Date(histEntry.timestamp).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                  </span>
                )}
              </div>
            </div>
            {i < STATUS_STEPS.length - 1 && (
              <div className={`tracker__line ${i < currentIdx ? "tracker__line--done" : ""}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────── */
export default function OrderConfirmation() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!orderId) { navigate("/products"); return; }

    (async () => {
      try {
        const result = await fetchOrderById(orderId);
        if (result?.success) {
          setOrder(result.data?.order || result.data);
          console.log("Fetched order details:", result.data.order);
        } else {
          setError(result?.message || "Order not found.");
        }
      } catch {
        setError("Failed to load order details.");
      } finally {
        setLoading(false);
      }
    })();
  }, [orderId, navigate]);

  /* ── Loading ── */
  if (loading) return (
    <>
      <style>{css}</style>
      <div className="oc-page">
        <div className="oc-loading">
          <div className="oc-spinner" />
          <p>Loading your order…</p>
        </div>
      </div>
    </>
  );

  /* ── Error ── */
  if (error || !order) return (
    <>
      <style>{css}</style>
      <div className="oc-page">
        <div className="oc-error">
          <span className="oc-error__icon">🌿</span>
          <h2>Order not found</h2>
          <p>{error || "We couldn't find this order."}</p>
          <Link to="/products" className="btn-primary">Continue Shopping</Link>
        </div>
      </div>
    </>
  );

  const isPaid = order.paymentStatus === "paid";
  const isCancelled = order.orderStatus === "Cancelled";

  return (
    <>
      <style>{css}</style>

      <div className="oc-page">

        {/* ── Breadcrumb ── */}
        <div className="oc-bc">
          <div className="oc-container">
            <Link to="/">Home</Link><span>›</span>
            <Link to="/orders">My Orders</Link><span>›</span>
            <span className="oc-bc__cur">Order #{order.orderNumber}</span>
          </div>
        </div>

        {/* ── Hero ── */}
        <div className={`oc-hero ${isCancelled ? "oc-hero--cancelled" : ""}`}>
          <div className="oc-container oc-hero__inner">
            {isCancelled ? (
              <div className="oc-hero__cancelled">
                <span className="oc-hero__cancelled-icon">✕</span>
                <div>
                  <h1 className="oc-hero__title">Order Cancelled</h1>
                  <p className="oc-hero__sub">Order #{order.orderNumber} has been cancelled.</p>
                </div>
              </div>
            ) : (
              <>
                <AnimatedCheck />
                <div className="oc-hero__text">
                  <h1 className="oc-hero__title">Order Confirmed!</h1>
                  <p className="oc-hero__sub">
                    Thank you! Your order <strong>#{order.orderNumber}</strong> has been placed successfully.
                  </p>
                  <p className="oc-hero__date">Placed on {formatDate(order.createdAt)}</p>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="oc-container oc-body">
          <div className="oc-layout">

            {/* ── LEFT COLUMN ── */}
            <div className="oc-left">

              {/* Order Tracker */}
              {!isCancelled && (
                <section className="oc-card">
                  <h2 className="oc-card__title">
                    <span className="oc-card__icon">📍</span> Order Status
                  </h2>
                  <OrderTracker
                    currentStatus={order.orderStatus}
                    statusHistory={order.statusHistory}
                  />
                </section>
              )}

              {/* Order Items */}
              <section className="oc-card">
                <h2 className="oc-card__title">
                  <span className="oc-card__icon">🛒</span>
                  Order Items
                  <span className="oc-card__badge">{order.orderItems?.length}</span>
                </h2>

                <div className="oc-items">
                  {order.orderItems?.map((item) => (
                    <div className="oc-item" key={item._id}>
                      <div className="oc-item__img">
                        {item.image
                          ? <img src={item.image} alt={item.name} />
                          : <span>🌿</span>}
                        {item.isFree && <span className="oc-item__free">FREE</span>}
                      </div>
                      <div className="oc-item__info">
                        <span className="oc-item__name">{item.name}</span>
                        <span className="oc-item__qty">Qty: {item.quantity}</span>
                      </div>
                      <div className="oc-item__price">
                        {item.isFree
                          ? <span className="oc-item__free-tag">Free</span>
                          : fmt(item.price * item.quantity)}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Shipping Address */}
              <section className="oc-card">
                <h2 className="oc-card__title">
                  <span className="oc-card__icon">📦</span> Delivery Address
                </h2>
                <div className="oc-address">
                  <p className="oc-address__name">{order.shippingAddress?.fullName}</p>
                  <p>{order.shippingAddress?.addressLine1}</p>
                  {order.shippingAddress?.addressLine2 && <p>{order.shippingAddress.addressLine2}</p>}
                  <p>{order.shippingAddress?.city}, {order.shippingAddress?.state} — {order.shippingAddress?.pincode}</p>
                  <p className="oc-address__phone">📞 +91 {order.shippingAddress?.phone}</p>
                </div>
              </section>

            </div>

            {/* ── RIGHT COLUMN ── */}
            <div className="oc-right">

              {/* Order Summary */}
              <section className="oc-card oc-summary">
                <h2 className="oc-card__title">
                  <span className="oc-card__icon">🧾</span> Order Summary
                </h2>

                <div className="oc-summary__rows">
                  <div className="oc-summary__row">
                    <span>Order Number</span>
                    <span className="oc-summary__val--mono">#{order.orderNumber}</span>
                  </div>
                  <div className="oc-summary__row">
                    <span>Order Date</span>
                    <span>{formatDate(order.createdAt)}</span>
                  </div>
                  <div className="oc-summary__row">
                    <span>Payment</span>
                    <span className={`oc-badge ${isPaid ? "oc-badge--green" : "oc-badge--yellow"}`}>
                      {isPaid ? "✓ Paid" : order.paymentStatus}
                    </span>
                  </div>
                  <div className="oc-summary__row">
                    <span>Status</span>
                    <span className={`oc-badge ${isCancelled ? "oc-badge--red" : "oc-badge--green"}`}>
                      {order.orderStatus}
                    </span>
                  </div>

                  {order.razorpayPaymentId && (
                    <div className="oc-summary__row">
                      <span>Payment ID</span>
                      <span className="oc-summary__val--mono oc-summary__val--small">{order.razorpayPaymentId}</span>
                    </div>
                  )}
                </div>

                <div className="oc-divider" />

                {/* Price Breakdown */}
                <div className="oc-price-rows">
                  <div className="oc-price-row">
                    <span>Subtotal</span><span>{fmt(order.subtotal)}</span>
                  </div>
                  {order.couponDiscount > 0 && (
                    <div className="oc-price-row oc-price-row--green">
                      <span>Coupon Discount {order.couponCode ? `(${order.couponCode})` : ""}</span>
                      <span>−{fmt(order.couponDiscount)}</span>
                    </div>
                  )}
                  {order.referralDiscount > 0 && (
                    <div className="oc-price-row oc-price-row--green">
                      <span>Referral Discount</span>
                      <span>−{fmt(order.referralDiscount)}</span>
                    </div>
                  )}
                  {order.loyaltyDiscount > 0 && (
                    <div className="oc-price-row oc-price-row--green">
                      <span>🎁 Loyalty Offer (50% off)</span>
                      <span>−{fmt(order.loyaltyDiscount)}</span>
                    </div>
                  )}
                  {order.offerDiscount > 0 && (
                    <div className="oc-price-row oc-price-row--green">
                      <span>Offer Discount</span><span>−{fmt(order.offerDiscount)}</span>
                    </div>
                  )}
                  <div className="oc-price-row">
                    <span>Shipping</span>
                    <span>
                      {order.shippingCharge === 0
                        ? <span className="oc-free">FREE</span>
                        : fmt(order.shippingCharge)}
                    </span>
                  </div>
                </div>

                <div className="oc-divider" />

                <div className="oc-total">
                  <span>Total Paid</span>
                  <span className="oc-total__val">{fmt(order.total)}</span>
                </div>
              </section>

              {/* Invoice */}
              {/* Invoice */}
              {order.invoice?.url && (
                <section className="oc-card oc-invoice">
                  <h2 className="oc-card__title">
                    <span className="oc-card__icon">📄</span> Invoice
                  </h2>
                  <p className="oc-invoice__sub">Your invoice is ready to download.</p>

                  <a href={order.invoice.url.replace("/upload/", "/upload/fl_attachment/")}
                    download={`invoice_${order.orderNumber || order._id}.png`}
                    className="oc-invoice__btn" >
                    ⬇ Download Invoice
                  </a>
                </section>
              )}

              {/* Actions */}
              <div className="oc-actions">
                <Link to="/products" className="oc-btn oc-btn--primary">
                  🌿 Continue Shopping
                </Link>
                <Link to="/orders" className="oc-btn oc-btn--outline">
                  View All Orders
                </Link>
              </div>

            </div>
          </div>
        </div>
      </div >
    </>
  );
}

/* ─────────────────────────────────────────────
   CSS
───────────────────────────────────────────── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=Jost:wght@300;400;500;600&display=swap');

:root {
  --c-primary:       #2d6a4f;
  --c-primary-light: #40916c;
  --c-primary-dark:  #1b4332;
  --c-accent:        #d4a017;
  --c-bg:            #f9f6f0;
  --c-surface:       #ffffff;
  --c-text:          #1c2b22;
  --c-muted:         #5a7262;
  --c-border:        #d4e6d5;
  --c-error:         #c0392b;
  --c-success:       #276749;
  --f-display:       'Cormorant Garamond', Georgia, serif;
  --f-body:          'Jost', sans-serif;
  --radius:          14px;
  --radius-sm:       8px;
  --ease:            cubic-bezier(.4,0,.2,1);
}

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: var(--f-body); background: var(--c-bg); color: var(--c-text); }
a { text-decoration: none; color: inherit; }

.oc-container { max-width: 1100px; margin: 0 auto; padding: 0 24px; }
.oc-page { min-height: 100vh;  margin-top:80px; }

/* ── BREADCRUMB ── */
.oc-bc {
  background: var(--c-surface); border-bottom: 1px solid var(--c-border);
  padding: 12px 0; font-size: .78rem; color: var(--c-muted);
}
.oc-bc .oc-container { display: flex; align-items: center; gap: 8px; }
.oc-bc a { color: var(--c-primary); }
.oc-bc a:hover { color: var(--c-primary-dark); }
.oc-bc__cur { color: var(--c-text); font-weight: 600; }

/* ── HERO ── */
.oc-hero {
  background: linear-gradient(135deg, var(--c-primary-dark), #1e4d35);
  padding: 48px 0; position: relative; overflow: hidden;
}
.oc-hero::before {
  content: ''; position: absolute; inset: 0;
  background: radial-gradient(ellipse 70% 120% at 90% 50%, rgba(64,145,108,.2), transparent 60%);
}
.oc-hero--cancelled {
  background: linear-gradient(135deg, #5c1a1a, #8b2e2e);
}
.oc-hero__inner {
  display: flex; align-items: center; gap: 28px;
  position: relative;
}
.oc-hero__text { flex: 1; }
.oc-hero__title {
  font-family: var(--f-display); font-size: clamp(1.8rem, 4vw, 2.8rem);
  font-weight: 600; color: #fff; margin-bottom: 8px;
}
.oc-hero__sub { font-size: .95rem; color: rgba(255,255,255,.75); font-weight: 300; margin-bottom: 4px; }
.oc-hero__sub strong { color: #fff; font-weight: 600; }
.oc-hero__date { font-size: .78rem; color: rgba(255,255,255,.45); }
.oc-hero__cancelled { display: flex; align-items: center; gap: 20px; }
.oc-hero__cancelled-icon {
  width: 56px; height: 56px; border-radius: 50%;
  background: rgba(255,255,255,.15); border: 2px solid rgba(255,255,255,.3);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.5rem; color: #fff; flex-shrink: 0;
}

/* ── ANIMATED CHECKMARK ── */
.acheck {
  width: 64px; height: 64px; flex-shrink: 0;
  animation: popIn .5s var(--ease) both;
}
@keyframes popIn { from{transform:scale(0);opacity:0} to{transform:scale(1);opacity:1} }
.acheck__svg { width: 100%; height: 100%; }
.acheck__circle {
  stroke: rgba(255,255,255,.5);
  stroke-width: 2;
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
  animation: strokeCircle .6s var(--ease) .2s forwards;
}
.acheck__tick {
  stroke: #fff;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  animation: strokeTick .4s var(--ease) .7s forwards;
}
@keyframes strokeCircle { to { stroke-dashoffset: 0; } }
@keyframes strokeTick { to { stroke-dashoffset: 0; } }

/* ── BODY LAYOUT ── */
.oc-body { padding: 32px 0 72px; }
.oc-layout {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 28px;
  align-items: start;
}

/* ── CARDS ── */
.oc-card {
  background: var(--c-surface); border: 1px solid var(--c-border);
  border-radius: var(--radius); padding: 24px 28px;
  margin-bottom: 20px;
  animation: cardIn .5s var(--ease) both;
}
.oc-left .oc-card:nth-child(1) { animation-delay: .05s; }
.oc-left .oc-card:nth-child(2) { animation-delay: .1s; }
.oc-left .oc-card:nth-child(3) { animation-delay: .15s; }
.oc-right .oc-card:nth-child(1) { animation-delay: .08s; }
.oc-right .oc-card:nth-child(2) { animation-delay: .13s; }
@keyframes cardIn { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:none} }

.oc-card__title {
  font-family: var(--f-display); font-size: 1.25rem;
  font-weight: 600; color: var(--c-primary-dark);
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 20px; padding-bottom: 14px;
  border-bottom: 1px solid var(--c-border);
}
.oc-card__icon { font-size: 1rem; }
.oc-card__badge {
  margin-left: auto; background: var(--c-primary); color: #fff;
  font-family: var(--f-body); font-size: .7rem; font-weight: 600;
  padding: 2px 9px; border-radius: 100px;
}

/* ── TRACKER ── */
.tracker {
  display: flex; align-items: flex-start;
  gap: 0; overflow-x: auto; padding-bottom: 4px;
}
.tracker--cancelled {
  display: flex; align-items: center; gap: 14px;
  padding: 16px; background: #fdf2f2; border-radius: var(--radius-sm);
}
.tracker__cancelled-icon {
  width: 36px; height: 36px; border-radius: 50%;
  background: var(--c-error); color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: .9rem; font-weight: 700; flex-shrink: 0;
}
.tracker__cancelled-text { font-size: .88rem; color: var(--c-error); font-weight: 500; }

.tracker__step { display: flex; flex-direction: column; align-items: center; gap: 8px; min-width: 72px; }
.tracker__dot {
  width: 36px; height: 36px; border-radius: 50%;
  border: 2px solid var(--c-border); background: var(--c-bg);
  display: flex; align-items: center; justify-content: center;
  font-size: .78rem; font-weight: 700; color: var(--c-muted);
  transition: all .3s var(--ease); flex-shrink: 0;
}
.tracker__step--done .tracker__dot {
  background: var(--c-primary); border-color: var(--c-primary); color: #fff;
}
.tracker__step--active .tracker__dot {
  background: var(--c-primary-dark); border-color: var(--c-primary-dark); color: #fff;
  box-shadow: 0 0 0 4px rgba(45,106,79,.18);
}
.tracker__info { display: flex; flex-direction: column; align-items: center; gap: 2px; }
.tracker__label { font-size: .72rem; font-weight: 600; color: var(--c-muted); text-align: center; }
.tracker__step--done .tracker__label,
.tracker__step--active .tracker__label { color: var(--c-primary-dark); }
.tracker__time { font-size: .65rem; color: var(--c-muted); }
.tracker__line {
  flex: 1; height: 2px; background: var(--c-border);
  margin: 17px 4px 0; min-width: 24px; transition: background .3s;
}
.tracker__line--done { background: var(--c-primary); }

/* ── ORDER ITEMS ── */
.oc-items { display: flex; flex-direction: column; gap: 16px; }
.oc-item { display: flex; align-items: center; gap: 14px; }
.oc-item__img {
  width: 52px; height: 52px; border-radius: 10px; flex-shrink: 0;
  background: linear-gradient(135deg, #f0f7f2, #e8f5ec);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.5rem; overflow: hidden; position: relative;
}
.oc-item__img img { width: 100%; height: 100%; object-fit: cover; }
.oc-item__free {
  position: absolute; bottom: 0; left: 0; right: 0;
  background: var(--c-primary); color: #fff;
  font-size: .55rem; font-weight: 700; text-align: center;
  padding: 2px 0; letter-spacing: .04em;
}
.oc-item__info { flex: 1; min-width: 0; }
.oc-item__name {
  display: block; font-size: .9rem; font-weight: 600;
  color: var(--c-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  margin-bottom: 3px;
}
.oc-item__qty { font-size: .75rem; color: var(--c-muted); }
.oc-item__price {

 font-size: 1.05rem;
  font-weight: 600; color: var(--c-primary-dark); white-space: nowrap;
}
.oc-item__free-tag {
  background: rgba(45,106,79,.1); color: var(--c-primary);
  font-size: .7rem; font-weight: 700; padding: 3px 10px;
  border-radius: 100px; font-family: var(--f-body);
}

/* ── ADDRESS ── */
.oc-address { font-size: .88rem; color: var(--c-text); line-height: 1.7; }
.oc-address__name { font-weight: 600; font-size: .95rem; margin-bottom: 2px; }
.oc-address__phone { color: var(--c-primary); margin-top: 6px; font-weight: 500; }

/* ── SUMMARY CARD ── */
.oc-summary__rows { display: flex; flex-direction: column; gap: 12px; margin-bottom: 4px; }
.oc-summary__row {
  display: flex; justify-content: space-between; align-items: center;
  font-size: .84rem; color: var(--c-muted);
}
.oc-summary__val--mono { font-family: monospace; font-size: .82rem; color: var(--c-text); font-weight: 600; }
.oc-summary__val--small { font-size: .72rem; }

.oc-badge {
  font-size: .72rem; font-weight: 700; padding: 3px 10px;
  border-radius: 100px; letter-spacing: .03em;
}
.oc-badge--green { background: rgba(45,106,79,.1); color: var(--c-primary); }
.oc-badge--yellow { background: rgba(212,160,23,.12); color: #b8860b; }
.oc-badge--red { background: rgba(192,57,43,.1); color: var(--c-error); }

.oc-divider { height: 1px; background: var(--c-border); margin: 16px 0; }

.oc-price-rows { display: flex; flex-direction: column; gap: 10px; }
.oc-price-row {
  display: flex; justify-content: space-between;
  font-size: .84rem; color: var(--c-muted);
}
.oc-price-row--green { color: var(--c-success); }
.oc-free {
  background: rgba(45,106,79,.08); color: var(--c-primary);
  font-size: .68rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: .06em; padding: 2px 8px; border-radius: 100px;
}

.oc-total {
  display: flex; justify-content: space-between; align-items: baseline;
  padding-top: 4px;
}
.oc-total > span:first-child { font-size: .88rem; font-weight: 700; color: var(--c-text); }
.oc-total__val {
 font-size: 1.6rem;
  font-weight: 600; color: var(--c-primary-dark);
}

/* ── INVOICE ── */
.oc-invoice__sub { font-size: .84rem; color: var(--c-muted); margin-bottom: 16px; }
.oc-invoice__btn {
  display: inline-flex; align-items: center; gap: 8px;
  font-family: var(--f-body); font-size: .82rem; font-weight: 600;
  letter-spacing: .06em; text-transform: uppercase;
  padding: 11px 22px; border-radius: 100px;
  background: var(--c-primary); color: #fff;
  border: none; cursor: pointer;
  transition: background .22s, transform .22s;
  text-decoration: none;
}
.oc-invoice__btn:hover { background: var(--c-primary-dark); transform: translateY(-1px); }

/* ── ACTIONS ── */
.oc-actions { display: flex; flex-direction: column; gap: 12px; margin-top: 4px; }
.oc-btn {
  width: 100%; padding: 14px; border-radius: 100px;
  font-family: var(--f-body); font-size: .86rem; font-weight: 700;
  letter-spacing: .06em; text-transform: uppercase;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  transition: all .22s var(--ease); text-decoration: none;
  border: none; cursor: pointer;
}
.oc-btn--primary {
  background: var(--c-primary); color: #fff;
  box-shadow: 0 4px 16px rgba(45,106,79,.28);
}
.oc-btn--primary:hover { background: var(--c-primary-dark); transform: translateY(-1px); }
.oc-btn--outline {
  background: transparent; color: var(--c-primary);
  border: 2px solid var(--c-border);
}
.oc-btn--outline:hover { border-color: var(--c-primary); background: rgba(45,106,79,.04); }

/* ── LOADING / ERROR ── */
.oc-loading, .oc-error {
  min-height: 60vh; display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 16px;
  text-align: center; color: var(--c-muted);
}
.oc-spinner {
  width: 40px; height: 40px; border-radius: 50%;
  border: 3px solid var(--c-border); border-top-color: var(--c-primary);
  animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.oc-error__icon { font-size: 3rem; }
.oc-error h2 { font-family: var(--f-display); font-size: 1.6rem; color: var(--c-text); }
.btn-primary {
  display: inline-flex; align-items: center; gap: 8px;
  font-family: var(--f-body); font-size: .82rem; font-weight: 600;
  letter-spacing: .07em; text-transform: uppercase;
  padding: 12px 28px; border-radius: 100px; border: none;
  background: var(--c-primary); color: #fff; cursor: pointer;
  transition: background .22s, transform .22s; text-decoration: none;
}
.btn-primary:hover { background: var(--c-primary-dark); transform: translateY(-1px); }

/* ── RESPONSIVE ── */
@media (max-width: 900px) {
  .oc-layout { grid-template-columns: 1fr; }
  .oc-right { display: contents; }
  .oc-summary { order: -1; }
}
@media (max-width: 600px) {
  .oc-container { padding: 0 16px; }
  .oc-hero { padding: 32px 0; }
  .oc-hero__inner { gap: 18px; }
  .acheck { width: 48px; height: 48px; }
  .oc-hero__title { font-size: 1.6rem; }
  .oc-card { padding: 18px 16px; }
  .tracker { gap: 0; }
  .tracker__step { min-width: 56px; }
  .tracker__label { font-size: .65rem; }
}
`;